import { FormikErrors, FormikTouched } from 'formik';
import { MeasurementFieldConfig } from 'src/models/measurement/measurement';
import { TrainingInfoField, User } from 'src/models/user/_types';
import { ColorHex, Identifier, Timestamp } from 'src/types/utilityTypes';

import { RoleProps } from '../trainer/_types';

export enum FeatureFlag {
  ArchivedPackages = 'Archived Packages',
  ArchivedServices = 'Archived Services',
  FamilySharing = 'Family Sharing',
  LocationSplitting = 'Location Splitting',
  UsePackageLocation = 'Use Package Location',
  ProductsUseClientLocation = 'Products Use Client Location',
  GoogleCalendarIntegration = 'Google Calendar Integration',
  MergeUsers = 'Merge Users',
  InstantPayouts = 'Instant Payouts',
  Klarna = 'Klarna',
  Classpass = 'Classpass',
  StripeMigration = 'Stripe Migration',
  Quickbooks = 'Quickbooks',
  Mindbody = 'Mindbody',
  IframeGroupTabs = 'Iframe Group Tabs',
  SMS = 'SMS',
  StaffTaskManagement = 'Staff Task Management',
  HideOnlineTraining = 'Hide Online Training',
  EnableCaptcha = 'Enable Captcha',
  NoAssessmentRedirect = 'No Assessment Redirect',
  DisableCardValidation = 'Disable Card Validation',
  HideWatermark = 'Hide Powered By Exercise Com Watermark',
  DisableDirectDebitEmails = 'Disable Direct Debit Emails',
  EmailMarketing = 'Email Marketing',
  ApiAccess = 'Api Access',
  TimeTracking = 'Time Tracking',
  Webhooks = 'Webhooks',
  EnableNewAppFeatures = 'Enable New App Features',
  RequireExerciseVideos = 'Require Exercise Videos',
  HideCashAndCheckInStaffCheckout = 'Hide Cash and Check in Staff Checkout',
  DisableBillingInfo = 'Disable Billing Info',
  DisableProfileAddress = 'Disable Profile Address',
  AlternateExerciseVideos = 'Alternate Exercise Videos',
  NewDashboard = 'New Dashboard',
  WearableIntegration = 'Wearable Integration',
  WaiversRedesign = 'Waivers Redesign',
  GuidedLogger = 'Guided Logger',
  Onboarding = 'Onboarding',
  KisiAppIntegration = 'Kisi App Integration',
  IntercomAppIntegration = 'Intercom App Integration',
  TapToPayIntegration = 'Tap To Pay Integration',
  RequiredWaiverAppAlerts = 'Required Waiver App Alerts',
  DisableLoggingBodyweight = 'Disable Logging Bodyweight',
  DisableLoggingHeight = 'Disable Logging Height',
  Afterpay = 'Afterpay',
  BelongsToContainer = 'Belongs to Container',
  EnableLocationAndServiceCode = 'Enable Location And Service Code',
  CardTokenization = 'Card Tokenization',
  DisableCashAndCheck = 'Disable Cash and Check',
  ResourceManagement = 'Resource Management',
  StripeTax = 'Stripe Tax',
  StripeTaxSettings = 'Stripe Tax Settings',
  MemberVsNonMemberPricing = 'Member vs Non-Member Pricing',
  SkipMicrodeposits = 'Skip Microdeposits',
  HideLeaderboardAssessmentResults = 'Hide Leaderboard Assessment Results',
  UseSendingEmailForMessages = 'Use Sending Email For Messages',
  RequireInviteLinkForSiteSignup = 'Require Invite Link For Site Signup',
  PDFWatermarking = 'PDF Watermarking',
  ConnectToStripe = 'Connect to Stripe',
  ContainerHost = 'Container Host',
  LegacyStrava = 'Legacy Strava',
  EnableNutrition = 'Enable Nutrition',
  ContainerApp = 'Container App',
  IsContainerApp = 'Is Container App',
  Pendo = 'Pendo',
  ReactPlanCreator = 'React Plan Creator',
  NewAutomations = 'New Automations',
  DreamcoatFrame = 'Dreamcoat Frame',
  DreamcoatOptIn = 'Dreamcoat Opt In',
  NewJournal = 'New Journal',
  DarkMode = 'Dark Mode',
  OutstandingServiceRevenueReport = 'Outstanding Service Revenue Report',
  LowServiceBalanceFilter = 'Low Service Balance Filter',
  StaffPayrollReport = 'Staff Payroll Report',
  AssignDashboardsToStaffMembers = 'Assign Dashboards to Staff Members',
  UpsellLinks = 'Upsell Links',
  AIAssistantCoach = 'AI Assistant Coach',
}

export const FEATURE_FLAG_DESCRIPTIONS = {
  [FeatureFlag.ArchivedPackages]:
    'Shows archived packages and related actions on the packages list (web).',
  [FeatureFlag.ArchivedServices]:
    'Shows archived services and related actions on the services list (web).',
  [FeatureFlag.FamilySharing]: 'Allow dependents',
  [FeatureFlag.LocationSplitting]:
    'Allows payment splitting to separate stripe accounts.',
  [FeatureFlag.UsePackageLocation]:
    "Prefers the package's location for the Stripe connected account when splitting payment destinations.",
  [FeatureFlag.ProductsUseClientLocation]:
    "Can use the client's home location account when splitting payment destinations on product purchases.",
  [FeatureFlag.GoogleCalendarIntegration]:
    'Shows Google Calendar in platform app integrations (Apps / connections).',
  [FeatureFlag.MergeUsers]: 'Allow the platform to merge accounts',
  [FeatureFlag.InstantPayouts]:
    'Controls instant-payout-related options surfaced for the platform in billing settings (v2 API).',
  [FeatureFlag.Klarna]:
    'Offers Klarna at client checkout when the processor and cart support it.',
  [FeatureFlag.Classpass]:
    'Enables ClassPass-related scheduling and location options where implemented.',
  [FeatureFlag.StripeMigration]:
    'Shows Stripe account migration / setup tools in Apps for platforms moving Stripe configuration.',
  [FeatureFlag.Quickbooks]: 'Enables QuickBooks connect and sync in Apps.',
  [FeatureFlag.Mindbody]:
    'Enables Mindbody import and connection entry points (Apps / platform admin).',
  [FeatureFlag.IframeGroupTabs]:
    'Allows iframe-type custom tabs on group settings (otherwise filtered out).',
  [FeatureFlag.SMS]:
    'Turns on the platform SMS feature flag used with SMS billing and messaging capabilities.',
  [FeatureFlag.StaffTaskManagement]:
    'Enables staff task management (My Tasks, task audit log, and related APIs).',
  [FeatureFlag.HideOnlineTraining]:
    'Hides online training upsells and purchase paths in client-facing web areas.',
  [FeatureFlag.EnableCaptcha]:
    'Requires captcha on applicable unauthenticated API requests.',
  [FeatureFlag.NoAssessmentRedirect]:
    'After an assessment is submitted in v3, skips the automatic redirect to the configured return URL.',
  [FeatureFlag.DisableCardValidation]:
    'Skips Stripe card validation when attaching cards to an account.',
  [FeatureFlag.HideWatermark]:
    'Hides the "Powered by Exercise.com" footer branding on white-label web.',
  [FeatureFlag.DisableDirectDebitEmails]:
    'Suppresses ACH direct-debit-related automation emails for the platform.',
  [FeatureFlag.EmailMarketing]:
    'Enables custom BeeFree email templates, one-off and scheduled sends, and email delivery history.',
  [FeatureFlag.ApiAccess]: 'Allows creating API keys for the platform.',
  [FeatureFlag.TimeTracking]:
    'Shows staff time tracking (clock in/out) in trainer web where implemented.',
  [FeatureFlag.Webhooks]:
    'Allows configuring outbound webhooks for the platform.',
  [FeatureFlag.EnableNewAppFeatures]:
    'Unlocks newer mobile app capabilities (measurements, banners, plans UI, etc.).',
  [FeatureFlag.RequireExerciseVideos]:
    'Requires a video (URL, attachment, or stream) when creating or updating exercises.',
  [FeatureFlag.HideCashAndCheckInStaffCheckout]:
    'Omit cash and check from the client payment method selector for staff checkout.',
  [FeatureFlag.DisableBillingInfo]:
    'When on, hides billing info sections for non-head staff in account UI.',
  [FeatureFlag.DisableProfileAddress]:
    'Hides address fields on trainer profile forms.',
  [FeatureFlag.AlternateExerciseVideos]:
    'Shows alternate exercise video source options in the exercise editor (web).',
  [FeatureFlag.NewDashboard]:
    'Uses the React trainer dashboard route instead of the legacy dashboard.',
  [FeatureFlag.WearableIntegration]:
    'Enables Terra/wearable integrations (nutrition, exercises, connections, mobile branding).',
  [FeatureFlag.WaiversRedesign]:
    'Uses the React waivers experience and API paths instead of legacy flows.',
  [FeatureFlag.GuidedLogger]:
    'Allows defaulting workouts to the guided logger experience in creator / apps.',
  [FeatureFlag.Onboarding]:
    'Shows onboarding checklist entry points and progress in the React app shell.',
  [FeatureFlag.KisiAppIntegration]:
    'Exposes Kisi access control integration to mobile apps (runtime config).',
  [FeatureFlag.IntercomAppIntegration]:
    'Exposes Intercom integration to mobile apps (runtime config).',
  [FeatureFlag.TapToPayIntegration]:
    'Exposes Stripe Tap to Pay on iPhone to mobile apps (runtime config).',
  [FeatureFlag.RequiredWaiverAppAlerts]:
    'Allows server-driven waiver reminder payloads for clients with outstanding required waivers.',
  [FeatureFlag.DisableLoggingBodyweight]:
    'Hides bodyweight from profile / measurement logging where that UI is shown.',
  [FeatureFlag.DisableLoggingHeight]:
    'Hides height from profile / measurement logging where that UI is shown.',
  [FeatureFlag.Afterpay]:
    'Offers Afterpay at client checkout when address and processor requirements are met.',
  [FeatureFlag.BelongsToContainer]:
    'Marks the platform as a Container App child: shared Firebase RTDB and container runtime with the singleton host.',
  [FeatureFlag.EnableLocationAndServiceCode]:
    'Shows location and service code fields on package and service forms.',
  [FeatureFlag.CardTokenization]:
    'Uses legacy Stripe.js card tokenization instead of PaymentMethod + SetupIntent in Ember card flows.',
  [FeatureFlag.DisableCashAndCheck]:
    'Disables cash and check payment paths in legacy Ember (v2) checkout.',
  [FeatureFlag.ResourceManagement]:
    'Enables rooms/resources on services, calendar events, and location admin actions.',
  [FeatureFlag.StripeTax]: 'Enables Stripe Tax at checkout.',
  [FeatureFlag.StripeTaxSettings]:
    'Shows Stripe Tax product defaults and tax behavior in billing forms and Stripe account sessions.',
  [FeatureFlag.MemberVsNonMemberPricing]:
    'Shows alternate package pricing tiers with member eligibility rules in package configuration.',
  [FeatureFlag.SkipMicrodeposits]:
    'For manual bank account setup, skips microdeposits when supported.',
  [FeatureFlag.HideLeaderboardAssessmentResults]:
    'Suppresses assessment results on leaderboard-style displays.',
  [FeatureFlag.UseSendingEmailForMessages]:
    'Uses the platform "sending" email as the outbound identity for certain message-related emails.',
  [FeatureFlag.RequireInviteLinkForSiteSignup]:
    'Requires an invite link in the cart when adding people or associations for gated site signup.',
  [FeatureFlag.PDFWatermarking]:
    'Watermarks generated user-specific workout plan PDFs for clients (not trainers) when enabled.',
  [FeatureFlag.ConnectToStripe]: 'Allows platform to connect to Stripe.',
  [FeatureFlag.ContainerHost]:
    'Marks the platform as a Container App host (shared mobile build, Firebase, runtime for child platforms).',
  [FeatureFlag.LegacyStrava]:
    'Shows the legacy Strava connection card in Apps when Wearables is off.',
  [FeatureFlag.EnableNutrition]:
    'Enables the nutrition tracking experience for the platform.',
  [FeatureFlag.ContainerApp]:
    'Marks the platform as a Container App child site.',
  [FeatureFlag.IsContainerApp]:
    'Alias flag for Container App child platform detection.',
  [FeatureFlag.Pendo]:
    'Allows loading Pendo product analytics in web and exposes the flag to mobile runtime config.',
  [FeatureFlag.ReactPlanCreator]:
    'Allows access to the React workout plan creator routes.',
  [FeatureFlag.NewAutomations]:
    'Routes automations to the React automations experience.',
  [FeatureFlag.DreamcoatFrame]:
    'Switches to a Daxko branded app frame and navigation',
  [FeatureFlag.DreamcoatOptIn]:
    'Lets staff individually opt in to the new Dreamcoat navigation via a toggle (does not force it like Dreamcoat Frame).',
  [FeatureFlag.NewJournal]:
    'Routes journal links to the React journal at /me/journal and /clients/:clientId/journal.',
  [FeatureFlag.DarkMode]:
    'Allows users to switch between light and dark mode in the React app shell.',
  [FeatureFlag.OutstandingServiceRevenueReport]:
    'Shows the Outstanding Service Revenue report in the reports list.',
  [FeatureFlag.LowServiceBalanceFilter]:
    'Shows the Low Service Balance option in client Custom Status filters (clients list and reports).',
  [FeatureFlag.StaffPayrollReport]:
    'Enables staff payroll summary and detail reports, exports, and pay period setup and locking.',
  [FeatureFlag.AssignDashboardsToStaffMembers]:
    'Controls rollout availability for assigning dashboards to staff members through a global release toggle.',
  [FeatureFlag.UpsellLinks]:
    'Shows the Upsells row action on group, plan, package, and product lists, linking to the upsell pages for each sellable item.',
  [FeatureFlag.AIAssistantCoach]:
    'Enables AI Assistant Coach functionality for the platform.',
} as const satisfies Record<FeatureFlag, string>;

export interface ProfileMenuItem {
  id?: number;
  name: string;
  title: string;
  url: string;
  icon: string;
  visibility: string[];
  minVersion?: string;
  maxVersion?: string;
  platform?: string;
  groupId?: string;
  public?: boolean;
}

export interface AppDrawerMenuItem {
  id?: number;
  name: string;
  target: string;
  public: boolean;
  visibility: string[];
  minVersion?: string;
  maxVersion?: string;
  icon?: string;
  platform?: string;
  groupId?: string;
}

export interface AppNavigationTab {
  id?: number;
  name: string;
  title: string;
  url: string;
  icon: string;
  visibility: string[];
  minVersion?: string;
  maxVersion?: string;
  platform?: string;
  groupId?: string;
  public?: boolean;
}

export interface LimitBookingService {
  maxVisits: number;
  serviceIds: string[];
  visitsInterval: string;
  visitsIntervalCount: number;
}

export interface AppDashboardCarouselItem {
  name: string;
  description: string;
  title: string;
  imageUrl: string;
  targetUrl: string;
  groupId: string | null;
  minVersion?: string;
  maxVersion?: string;
}

export interface IAPItem {
  type: 'WorkoutPlan' | 'Group' | 'User' | null;
  id: string | null;
  iapId: string | null;
  interval?: number | null;
  intervalType?: string | null;
}

export interface AppLoggerNavItem {
  id: string;
  name: string;
  icon: string;
}

export interface ReferAFriendUrl {
  title?: string;
  url: string;
}

export interface ReferAFriendSettings {
  title?: string;
  subtitle?: string;
  smsText?: string;
  urls?: ReferAFriendUrl[];
}

export interface CloudflareDnsRecord {
  name: string;
  status: 'completed' | 'not_completed';
  type: 'CNAME';
  value: string;
}

export interface CloudflareStatus {
  cloudflare?: {
    customHostnameId?: string;
    sslStatus?: string;
    status?: string;
  };
  completed: boolean;
  dns?: {
    acme?: boolean | null;
  };
  domain?: string;
  records: CloudflareDnsRecord[];
  status: 'completed' | 'not_completed';
}

export interface Platform {
  featureFlags?: FeatureFlag[];
  /**
   * featureFlags combined with global release toggles (read-only): globally
   * "off" flags removed, globally "on" flags added. Use for feature checks;
   * featureFlags stays raw for editing the per-tenant array.
   */
  effectiveFeatureFlags?: FeatureFlag[];
  id: Identifier;
  brand: string;
  subdomain: string;
  host: string;
  subdomainUrl: string;
  alternateHost?: string;
  cloudflareConfigured?: boolean;
  cloudflareStatus?: CloudflareStatus;
  logoUrl: string;
  darkmodeLogoUrl?: string;
  iconUrl: string;
  appIcon?: string;
  twilioPhoneNumber: string;
  smsAvailable?: boolean;
  /** When true, show SMS/Twilio consent UI on phone fields (includes pending 10DLC, not only approved SMS). */
  smsConsentCheckboxVisible?: boolean;
  trainerId: User['id'];
  trainerName: string;
  trainerEmail: string;
  clientTags: string[];
  trainerTags?: string[];
  videoTags?: string[];
  groupResourceTags?: string[];
  groupAssessmentTags?: string[];
  serviceTags?: string[];
  packageTags?: string[];
  productTags?: string[];
  exerciseTags?: string[];
  workoutLabelTags?: { name: string; image?: string }[];
  createdAt: Timestamp;
  updatedAt: Timestamp;
  customExerciseMeasurements: string[];
  disableClientCheckin: boolean;
  cancellationPolicyHours: number;
  disableRescheduling?: boolean;
  disableLateCancellationRescheduling?: boolean;
  destinationCharges: boolean;
  disableEditProfileImage?: boolean;
  requireChargedBy?: boolean;
  country: string;
  platformTabs: {
    mainNav: PlatformTab[];
    trainerAccount: PlatformTab[];
    trainerDashboard: PlatformTab[];
    clientManagement: PlatformTab[];
    clientDashboard: PlatformTab[];
    clientAccount: PlatformTab[];
  };
  trainingInfoFields: TrainingInfoField[];
  statsList: MeasurementFieldConfig[];
  defaultHealthMetrics: string[];
  taxRates: TaxRate[];
  stripeAccountId: string;
  paymentMethodOrder: string[];
  newClientRequiredFields?: {
    id: string;
    name: string;
  }[];

  mainNavBgColor?: string;
  mainNavTextColor?: string;
  subNavBgColor?: string;
  subNavTextColor?: string;
  mainNavHighlightColor?: string;
  primaryButtonTextColor?: string;
  dropdownTextColor?: string;
  primaryButtonColor?: string;
  linkColor?: string;
  highlightColor?: string;
  supersetColor?: string;
  circuitColor?: string;
  supersetBorder?: string;
  circuitBorder?: string;
  appPrimaryColor?: string;
  measurementColors?: string;
  contactUrl?: string;
  website?: string;
  planTab?: string;
  customCss?: string;
  webAnalytics?: string;
  headWebAnalytics?: string;
  sendMonthlyEmails?: boolean;
  otpPlatform?: boolean;
  customSignup?: string;
  limitLoginAttempts?: boolean;
  useDarkMode?: boolean;
  metricPlatform?: boolean;
  hideCalendars?: boolean;
  showActivity?: boolean;
  disabledTabs?: string;
  allowsNonBilledClients?: boolean;
  showItemsOnThankYouPage?: boolean;
  showTrainingOnThankYouPage?: boolean;
  clientWelcome?: string;
  messagePlanPurchasers?: boolean;
  landingPageGroupOrder?: string;
  landingPageSectionOrder?: string;
  landingPagePlanOrder?: string;
  failedCheckout?: string;
  useFinderFilters?: boolean;
  landingRedirect?: string;
  welcomeNewUser?: string;
  firstWorkoutEmail?: boolean;
  alternateLanding?: string;
  sendingEmail?: string;
  sendingName?: string;
  healthInfoMetrics?: string[];
  measurementsOrder?: string[];
  defaultMeasurements?: string[];
  defaultMeasurementTags?: string[];
  requiredAssessment?: string;
  requiredSequence?: string;
  requireWaiver?: boolean;
  stripePlan?: string;
  clientLicensePlan?: string;
  defaultStripePlan?: string;
  trainerProfileFields?: string[];

  blockSitesToAddRoles?: boolean;
  blockSitesToEditRoles?: boolean;
  disableEditingLifecycleStages?: boolean;
  defaultChildPlatformRoles?: PlatformRole[];
  enableGuestCheckout?: boolean;
  allowUsersToSeeAccountBalance?: boolean;
  productPageForceFilters?: string[];
  macroCalculatorSettings?: MacroCalculatorSettings;
  disablePlatformSettingsTab?: boolean;
  disablePlatformSchedulingTab?: boolean;
  disablePlatformClientsTab?: boolean;
  subPlatformTabs?: {
    mainNav: PlatformTab[];
    trainerAccount: PlatformTab[];
    trainerDashboard: PlatformTab[];
    clientManagement: PlatformTab[];
    clientDashboard: PlatformTab[];
    clientAccount: PlatformTab[];
  };
  disableNavigationClientManagementTabs?: boolean;
  disableNavigationClientDashboardTabs?: boolean;
  disableNavigationClientAccountTabs?: boolean;
  disableNavigationTrainerDashboardTabs?: boolean;
  disableNavigationTrainerAccountTabs?: boolean;
  disableNavigationMainNavTabs?: boolean;
  disabledReports?: string[];

  // Feature Flag Settings
  supportedEntryTypes?: string[];
  defaultWorkoutType?: string;
  optInMailing?: boolean;
  disableLoggingCustomWorkouts?: boolean;
  disableEditingWorkouts?: boolean;
  disableLoggingFromUserPlans?: boolean;
  disableLoggingFromScheduledWorkouts?: boolean;
  disableLoggingFromFavoriteWorkouts?: boolean;
  disableLoggingFromRecentWorkouts?: boolean;
  showSetsInSummary?: boolean;
  makePurchasersClients?: boolean;
  makeAllAccountsClients?: boolean;
  hideAppCta?: boolean;
  useNavTextColorOnMobile?: boolean;
  trackCheckoutReferers?: boolean;
  disableAutostartTimer?: boolean;
  showToolbarTextLabels?: boolean;
  hideTotalTime?: boolean;
  hideTotalWeight?: boolean;
  hideTotalDistance?: boolean;
  hideTotalCalories?: boolean;
  requireBarcodesForCheckin?: boolean;
  allowCommissionWithFreePackages?: boolean;

  // Kiosk Check-in Settings
  allowCheckIn?: boolean;
  checkinFailureSound?: boolean;
  checkInPayment?: boolean;
  checkInServices?: boolean;
  checkInAssessment?: string | null;
  blockCheckinWhenBillingDelinquent?: boolean;
  blockCheckinWhenRequiredDocumentsUnsigned?: boolean;
  billingCheckinDelinquencyFollowupEnabled?: boolean;
  billingCheckinDelinquencyFollowupIntervalDays?: number | string;
  blockCheckinWhenRequiredFormsIncomplete?: boolean;

  theme: {
    primary: ColorHex;
    secondary: ColorHex;
    neutral: ColorHex;
    forcedTheme: 'light' | 'dark' | null;
  };

  webTheme: {
    primary: ColorHex;
    linkColor: ColorHex;
    highlightColor: ColorHex;
    subNavBgColor: ColorHex;
    subNavTextColor: ColorHex;
    mainNavBgColor: ColorHex;
    mainNavTextColor: ColorHex;
    mainNavHighlightColor: ColorHex;
    primaryButtonColor: ColorHex;
    primaryButtonTextColor: ColorHex;
    dropdownTextColor: ColorHex;
    supersetColor: ColorHex;
    supersetBorder: ColorHex;
    circuitBorder: ColorHex;
    mainChromeTextColor: ColorHex;
    blockBackgroundColor: ColorHex;
    supersetBackgroundColor: ColorHex;
    circuitBackgroundColor: ColorHex;
  };

  // admin fields
  currentIosVersion: string;
  currentAndroidVersion: string;
  enableFbm: boolean;
  waiver: string;
  waiverFields: { type: string; label: string }[];
  nutritionNotesTemplate: string;
  /** Default body when notifying clients about nutrition updates (platform-wide template). */
  nutritionEmail?: string;
  defaultCurrency: string;
  doNotDeductLateCancel: boolean;
  useStripeExpress: boolean;
  otherPaymentMethodLabels: string[];
  connectedTo: string[];
  iosArn: string;
  androidArn: string;
  pushCertificateExpiresAt: string;

  // White Label settings
  contactPhone?: string;
  mailingAddress?: string;
  hoursOfOperation?: string;
  supportEmailAddress?: string;
  mailingList?: string;
  maxWeeks?: string;
  maxLocations?: string;
  platformType?: string;
  parentId?: string | number;
  enableHq?: boolean;
  emailPreferencesLink?: string;
  viewAllTrainerMessages?: boolean;
  disableEmailExceptions?: string[];
  trainersToMessage?: Identifier[];
  disableRepMax?: boolean;
  autoFillWorkoutWeights?: boolean;
  noCcForTrials?: boolean;
  cancelTrialOnPaymentFailure?: boolean;
  subscriptionInvoiceRetryBehavior?: string;
  hideProfileStats?: boolean;
  hideBlockTitles?: boolean;
  hideMyPerformance?: boolean;
  thankYouPlans?: string[];
  thankYouGroups?: string[];
  hideClientLanding?: boolean;
  landingGroups?: string[];
  landingProducts?: string[];
  landingWorkoutPlans?: string[];
  landingPackages?: string[];
  packageLandingTags?: string[];
  publicExerciseTags?: string[];
  hideExerciseFilters?: string[];
  freeUserGroup?: string;
  useLongCheckout?: boolean;
  darkModeForCheckout?: boolean;
  requiredWaiverAppText?: string;
  customTerms?: string;
  requireCustomTerms?: boolean;
  reasonsForCancellation?: { label: string }[];
  cancellationMessage?: string;
  showOtherPlansOnIndexPage?: string;
  showCollectionScroller?: boolean;
  showScheduleTime?: string;
  calendarSlotLength?: number;
  excludeFromFbm?: Identifier[];
  freeNoshowVisits?: string;
  freeClassPackage?: boolean;
  freeClasses?: string;
  excludeNoShowsFromLimitBooking?: boolean;
  excludeLateCancellationsFromLimitBooking?: boolean;
  firstDayOfWeek?: number;
  requireTrainerForBooking?: boolean;
  serviceBalanceNoPrepayment?: boolean;
  bookingCalendar?: boolean;
  hideSpotsRemaining?: boolean;
  forceDependent?: boolean;
  bookPackageSessions?: boolean;
  useUserLocation?: boolean;
  showFullSessions?: boolean;
  showClosedSessions?: boolean;
  bookingFilters?: string[];
  bookingAvailableDefault?: string;
  forceBookingFilters?: string[];
  servicesOrder?: string[];
  staffOrder?: string[];
  hideTrainersFromBooking?: number[];
  packageFilters?: string[];
  forcePackageFilters?: string[];
  finderItems?: {
    itemId: number;
    itemType: 'product' | 'package' | 'plan' | 'group';
  }[];
  finderItemTypes?: {
    id: 'all' | 'product' | 'package' | 'plan' | 'group';
    name?: string;
    hidden?: boolean;
  }[];
  checkoutTags?: string[];
  limitBooking?: boolean;
  limitBookingDays?: string;
  limitBookingFutureDays?: string;
  limitBookingServices?: LimitBookingService[];
  gymCheckIn?: boolean;
  openGymServiceId?: number;
  checkInSearch?: string;
  onlineTrainingDestinationType?: string;
  onlineTrainingDestinationId?: string;
  clientThankYouMessage?: string;
  clientThankYouPage?: string;
  hideAlerts?: string[];
  redAlerts?: string[];
  useSubtrainerAsSender?: boolean;
  doubleEachSideWorkoutWeights?: boolean;

  // invoice settings
  businessProfile: {
    name: string | null;
    supportAddress: {
      city: string | null;
      state: string | null;
      postalCode: string | null;
      country: string | null;
      line1: string | null;
      line2: string | null;
    };
    supportEmail: string | null;
    supportPhone: string | null;
    url: string | null;
    supportUrl: string | null;
  };
  receiptFooter: string | null;
  receiptCustomFields: ReceiptCustomField[];

  // terminal stuff
  stripeLocationId: string;
  stripeLocationIds: string[];
  appleApiKey?: string | null;

  // apps stuff
  managedAppsNumber?: string;
  appBundleId?: string;
  appAndroidBundleId?: string;
  appAppleTeamId?: string;
  appFirebaseId?: string;
  appItcTeamId?: string;
  appItcTeamName?: string;
  appItcTeamDisplayName?: string;
  appItcAppPassword?: string;
  appProductAppId?: string;
  appShortName?: string;
  appLongName?: string;
  appDescription?: string;
  appAppleId?: string;
  iosDownloadLink?: string;
  androidDownloadLink?: string;
  unpublishedAt?: Timestamp;
  rusinVideosImportedAt?: Timestamp;
  twilio10dlcStatus?: string;
  twilio10DlcData?: any;
  twilio10dlcExcluded?: boolean;
  serviceAccountKey?: string;
  intercomAppId?: string;

  // app customization
  profileMenu?: ProfileMenuItem[];
  customAppMenu?: AppDrawerMenuItem[];
  customAppTabs?: AppNavigationTab[];
  termsOfServiceUrl?: string;
  privacyPolicyUrl?: string;

  platformRoles?: PlatformRole[];

  // platform node id
  fbmPlatformNodeId?: string;

  gtmContainerId?: string;

  // resource tag sort
  resourceTagHeaders?: string[];
  resourceTagSort?: string;
  resourceTaggedResourceSort?: string;

  // video tag sort
  videoTagSort?: string;
  videoTaggedVideoSort?: string;

  // Fee Settings
  transactionFeePer?: number;
  transactionFeeAmount?: number;
  fbmTransactionFeePer?: number;
  fbmTransactionFeeAmount?: number;
  invoiceFeePer?: number;
  invoiceFeeAmount?: number;

  // Bank Transfer Fees
  achTransactionFeePer?: number;
  achTransactionFeeAmount?: number;
  achTransactionCap?: number;
  auBecsDebitFeePer?: number;
  auBecsDebitFeeAmount?: number;
  auBecsDebitCap?: number;
  bacsDebitFeePer?: number;
  bacsDebitFeeAmount?: number;
  bacsDebitCap?: number;
  acssDebitFeePer?: number;
  acssDebitFeeAmount?: number;
  acssDebitCap?: number;
  sepaDebitFeePer?: number;
  sepaDebitFeeAmount?: number;
  sepaDebitCap?: number;

  // BNPL Fees
  klarnaEcommerceFeePer?: number;
  klarnaEcommerceFeeAmount?: number;
  klarnaEcommerceCap?: number;
  klarnaFbmFeePer?: number;
  klarnaFbmFeeAmount?: number;
  klarnaFbmCap?: number;
  afterpayClearpayFeePer?: number;
  afterpayClearpayFeeAmount?: number;
  afterpayClearpayCap?: number;

  // Stripe Fees
  dailyPayoutFee?: number;
  stripeCardFeePer?: number;
  stripeCardFeeAmount?: number;
  stripeInvoiceFeePer?: number;
  stripeInvoiceFeeAmount?: number;
  stripeSepaDebitFeeCap?: number;
  stripeSepaDebitFeePer?: number;
  stripeSepaDebitFeeAmount?: number;
  stripeAchFeeCap?: number;
  stripeAchFeePer?: number;
  stripeAchFeeAmount?: number;
  stripeBacsDebitFeeCap?: number;
  stripeBacsDebitFeePer?: number;
  stripeBacsDebitFeeAmount?: number;
  stripeAuBecsDebitFeeCap?: number;
  stripeAuBecsDebitFeePer?: number;
  stripeAuBecsDebitFeeAmount?: number;
  stripeAcssDebitFeeCap?: number;
  stripeAcssDebitFeePer?: number;
  stripeAcssDebitFeeAmount?: number;
  stripeKlarnaFeePer?: number;
  stripeKlarnaFeeAmount?: number;
  stripeAfterpayClearpayPer?: number;
  stripeAfterpayClearpayAmount?: number;

  // Stripe Tax
  stripeTaxCalculationPer?: number;
  stripeTaxTransactionPer?: number;
  stripeTaxSubscriptionPer?: number;
  stripeTaxFixedInvoiceFee?: number;

  // SMS Pricing
  smsPricePer?: number;
  smsFlatPrice?: number;
  smsFreeCount?: number;
  doNotTruncateSms?: boolean;
  hideSmsDefaultSessionPrompt?: boolean;

  // Client Pricing
  clientCount?: number;
  pricePerClient?: number;
  wearableConnectionAllotment?: number;
  locationStripePlan?: string;

  // App Dashboard
  appDashboard?: {
    list: {
      style: number;
    };
    carousel: {
      title: string;
      items: AppDashboardCarouselItem[];
    };
  };

  // In-App Purchases
  iap?: IAPItem[];

  // App Logger Navigation
  appLoggerNav?: AppLoggerNavItem[];
  appLoggerWorkoutNav?: AppLoggerNavItem[];

  // Domain Verification
  domainVerification?: {
    domain: string;
    verified: boolean;
    txtRecords?: string[];
  };

  // Images
  shareWorkoutImageUrl?: string;

  // Refer a Friend
  referAFriend?: ReferAFriendSettings;
  referAFriendImageUrl?: string;
  referAFriendImage?: string; // signed_id for upload
}

export interface PlatformRole {
  id: Identifier;
  name: string;
  permissions: RoleProps & {
    assignedDashboardIds?: Identifier[];
    disabledTabs: string[];
    disabledDashboardBoxes: string[];
    disabledEmails: string[];
    disabledDashboardNotifications: string[];
    headTrainerEmails: string[];
    reportsRole: string[];
  };
}

export interface ReceiptCustomField {
  name: string;
  value: string;
}

export interface PlatformTab {
  id: string;
  name: string;
  route: string;
  enabled: boolean;
  children?: PlatformTab[] | PlatformTabChildren;
  placement?: 'top' | 'bottom';
  reactRoute?: string;
  rel?: string;
  target?: string;
}

export interface PlatformTabChildren {
  items?: PlatformTab[];
  sections?: PlatformTabSection[];
}

export interface PlatformTabSection {
  id: string;
  label?: string;
  name?: string;
  collapsible?: boolean;
  children: PlatformTab[];
}

export interface StripeAccount {
  type: string;
  stripe_account_id: string;
  name: string;
}

export interface TenDlcError {
  type: 'ein_mismatch' | 'invalid_ein' | 'otp_pending';
  detectedAt: string;
  notificationCount: number;
  lastNotifiedAt?: string;
  details?: {
    failureReason?: string;
  };
}

export interface TenDlcDetails {
  twilio10dlcStatus: string;
  twilioMessagingService: string;
  twilio10dlcError?: TenDlcError | null;
  daysUntilSmsDisabled?: number | null;
}

export interface TenDlcCsv {
  company: string;
  website: string;
  businessType: string;
  businessIdType: string;
  businessId: string;
  regions: string;
  jobTitle: string;
  jobPosition: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  areaCode: string;
  areaCodeTwo: string;
}

export interface TaxRate {
  ach: boolean;
  card: boolean;
  ecommerce: boolean;
  fbm: boolean;
  id: number;
  label: string;
  percentage: number;
}

export interface Balance {
  object: string;
  available: {
    amount: number;
    currency: string;
    source_types: {
      bank_account: number;
      card: number;
    };
  }[];
  instant_available: {
    amount: number;
    currency: string;
    source_types: {
      bank_account: number;
      card: number;
    };
  }[];
  livemode: boolean;
  pending: {
    amount: number;
    currency: string;
    source_types: {
      bank_account: number;
      card: number;
    };
  }[];
}

export interface Transfer {
  id: number;
  amount: number;
  location: string;
  memo: string;
  createdAt: string;
  updatedAt: string;
}

export interface TransferRequest {
  destination: string;
  amount: number;
  memo: string;
  type: 'location' | 'trainer';
}

// Types for Formik errors
export interface ProfileMenuItemFormikErrors {
  profileMenu?: Array<FormikErrors<ProfileMenuItem>>;
}

export interface ProfileMenuItemFormikTouched {
  profileMenu?: Array<FormikTouched<ProfileMenuItem>>;
}

export interface AppDrawerMenuItemFormikErrors {
  drawerMenu?: Array<FormikErrors<AppDrawerMenuItem>>;
}

export interface AppDrawerMenuItemFormikTouched {
  drawerMenu?: Array<FormikTouched<AppDrawerMenuItem>>;
}

export interface AppNavigationTabFormikErrors {
  navigationTabs?: Array<FormikErrors<AppNavigationTab>>;
}

export interface AppNavigationTabFormikTouched {
  navigationTabs?: Array<FormikTouched<AppNavigationTab>>;
}

export interface BuildLog {
  id: string;
  platformId: number;
  triggeringUserId: number;
  triggeringUserName: string;
  repository: string;
  triggeredRef: string;
  platformType: 'ios' | 'android';
  ciProvider: 'github' | 'gitlab' | null;
  buildNumber: number;
  inputs: Record<string, any>;
  ciRunId: number | null;
  commitSha: string | null;
  appVersion: string | null;
  status: 'pending' | 'building' | 'complete' | 'error';
  statusMessage: string | null;
  ciStatus: string | null;
  ciConclusion: string | null;
  ciHtmlUrl: string | null;
  artifacts: Record<string, string> | null;
  createdAt: string;
  updatedAt: string;
}

export interface MacroCalculatorSettings {
  bmrFormula: 'harris_benedict' | 'mifflin_st_jeor';
  activityMultipliers: {
    sedentary: number;
    light: number;
    moderate: number;
    veryActive: number;
    extraActive: number;
  };
  macroMethod:
    | 'percentage_based'
    | 'body_weight_based'
    | 'hybrid_protein_bodyweight_fat_percentage';
  macroPercentages: {
    protein: number;
    fat: number;
    carbs: number;
  };
  macroPerKg: {
    protein: number;
    fat: number;
  };
  goalAdjustments: {
    lose: number;
    maintain: number;
    gain: number;
  };
  calorieLimits: {
    femaleMinimum: number;
    maleMinimum: number;
    maximum: number;
  };
  rounding: {
    calories: 'whole' | 'decimal';
    macros: 'whole' | 'oneDecimal';
  };
  customDescription?: string;
  customResultsInfo?: string;
}

export interface InviteLink {
  id: Identifier;
  platformId: Identifier;
  createdById: Identifier;
  redeemedById: Identifier;
  inviteItemId: Identifier;
  inviteItemType: string;
  expiresAt: Timestamp;
  redeemedAt: Timestamp;
  link: string;
}

export interface HideWorkoutSchedules {
  id: Identifier;
  platformId: Identifier;
  name: string;
  startDate: Timestamp;
  endDate: Timestamp;
  trainerIds: Identifier[];
  description?: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export interface RequireLocationResponse {
  requireLocation: boolean;
}
